import { SupportedLocale } from 'src/@types'
import { Application_Type, Criteria_Group_Config, Query_Root } from 'src/@types/graphql'
import { queryCriteriaGroupsByProcessesSortedByStatusAndId } from 'src/screens/administration/configuration-management/criteria-configuration/criteriaConfigQueries'
import { EvaluationCriteriaFormValues } from 'src/screens/administration/configuration-management/criteria-configuration/evaluation-criteria/edit/CriteriaConfigurationEvaluationCriteriaForm'
import { APPLICATION_TYPE_FACTSHEET, CONFIGURATION_STATUS, PROCESS, PROCESS_TYPE } from 'src/shared/constants/constants'
import { Option } from 'src/shared/form/control'
import { CheckboxData } from 'src/shared/form/control/CheckboxGroupField'
import { Client } from 'urql'

interface ApplicationTypesCheckboxDataPerProcess {
  pfKapApplicationTypesCheckbox: CheckboxData[]
  pfPgvApplicationTypesCheckbox: CheckboxData[]
  factsheetApplicationTypesCheckbox: CheckboxData[]
}

export const mapCriteriaGroupsListToOptionsList = (
  criteriaGroups: Criteria_Group_Config[],
  getMessage: (key: string, params?: Array<string | number>) => string,
  locale: SupportedLocale,
): Option[] =>
  criteriaGroups.map((cg) => {
    if (cg.group_active) {
      return {
        label: cg.names[locale],
        value: cg.id,
        sortNumber: cg.id,
      }
    }

    const inactiveLabel = getMessage('label.inactive')
    return {
      label: (cg.names[locale] || '') + ` (${inactiveLabel})`,
      value: cg.id,
      sortNumber: cg.id,
      disabled: true,
    }
  })

const mapApplicationTypesListToCheckboxDataList = (
  applicationTypes: Application_Type[],
  getMessage: (key: string, params?: Array<string | number>) => string,
): CheckboxData[] =>
  applicationTypes.map((aType) => ({
    label: getMessage(aType.key),
    value: aType.id,
  }))

export const mapFetchedApplicationTypesToCheckboxData = (
  fetchedApplicationTypes: Application_Type[],
  getMessage: (key: string, params?: Array<string | number>) => string,
): ApplicationTypesCheckboxDataPerProcess => {
  const fetchedPfKap = fetchedApplicationTypes.filter((aType) => aType.process === PROCESS.PF_KAP)
  const fetchedPfPgv = fetchedApplicationTypes.filter((aType) => aType.process === PROCESS.PF_PGV)

  // always use ORIENTATION_LIST application type for FACTSHEET
  const fetchedFactsheet = fetchedApplicationTypes
    .filter((aType) => aType.process === PROCESS.FACTSHEET)
    .filter((aType) => aType.code === APPLICATION_TYPE_FACTSHEET.ORIENTATION_LIST)

  return {
    pfKapApplicationTypesCheckbox: mapApplicationTypesListToCheckboxDataList(fetchedPfKap, getMessage),
    pfPgvApplicationTypesCheckbox: mapApplicationTypesListToCheckboxDataList(fetchedPfPgv, getMessage),
    factsheetApplicationTypesCheckbox: mapApplicationTypesListToCheckboxDataList(fetchedFactsheet, getMessage),
  }
}

export const getSelectedCriteriaGroupIdValue = (formValues: EvaluationCriteriaFormValues) => {
  switch (formValues.processAlloc) {
    case PROCESS.PF_KAP:
      return formValues.pfKapCriteriaGroup
    case PROCESS.PF_PGV:
      return formValues.pfPgvCriteriaGroup
    case PROCESS.FACTSHEET:
      return formValues.factsheetCriteriaGroup
    default:
      return null
  }
}

export const getSelectedApplicationTypeIdValues = (formValues: EvaluationCriteriaFormValues) => {
  switch (formValues.processAlloc) {
    case PROCESS.PF_KAP:
      return formValues.pfKapApplicationTypes
    case PROCESS.PF_PGV:
      return formValues.pfPgvApplicationTypes
    default:
      return []
  }
}

export const canUpdateEvaluationCriteriaStatus = (
  formValues: EvaluationCriteriaFormValues,
  criteriaGroups: Criteria_Group_Config[],
): boolean => {
  const criteriaGroupIdToSet = getSelectedCriteriaGroupIdValue(formValues)
  const isActiveSelected = formValues.status === CONFIGURATION_STATUS.ACTIVE

  if (isActiveSelected && criteriaGroupIdToSet != null) {
    let groupsToCheck: Criteria_Group_Config[] = []
    if (formValues.processAlloc === PROCESS.PF_KAP) {
      groupsToCheck = criteriaGroups.filter((cg) => cg.group_processes.includes(PROCESS.PF_KAP))
    } else if (formValues.processAlloc === PROCESS.PF_PGV) {
      groupsToCheck = criteriaGroups.filter((cg) => cg.group_processes.includes(PROCESS.PF_PGV))
    } else if (formValues.processAlloc === PROCESS.FACTSHEET) {
      groupsToCheck = criteriaGroups.filter((cg) => cg.group_processes.includes(PROCESS.FACTSHEET))
    }

    return groupsToCheck.some((cg) => cg.id === criteriaGroupIdToSet && cg.group_active)
  }

  return true
}

export const fetchCriteriaGroupsByProcesses = (urqlClient: Client) => async (processesParam: PROCESS_TYPE[]) => {
  const { data, error } = await urqlClient
    .query<
      {
        criteria_group_config: Query_Root['criteria_group_config']
      },
      {
        processes: Array<string>
      }
    >(queryCriteriaGroupsByProcessesSortedByStatusAndId, {
      processes: processesParam,
    })
    .toPromise()

  if (error || !data?.criteria_group_config) {
    throw new Error('Resource not found')
  }

  return {
    fetchedCriteriaGroups: data.criteria_group_config,
  }
}
